import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import api, { fmtZAR, PROJECT_TYPE_LABELS } from "@/lib/api";
import { PlusCircle, FolderKanban } from "lucide-react";

export default function ProjectsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? items : items.filter((p) => p.status === filter);
  const filters = ["all", "submitted", "estimate_ready", "quote_requested", "quote_ready", "approved", "in_progress", "completed"];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <PageHeader
          overline="Pipeline"
          title="Projects"
          description={user?.role === "customer" ? "Your active and past submissions." : "All projects across the platform."}
          actions={user?.role === "customer" ? (
            <Link to="/projects/new" data-testid="new-project-link" className="bq-orange-btn px-5 py-2.5 rounded-md font-semibold flex items-center gap-2">
              <PlusCircle size={16} /> New project
            </Link>
          ) : null}
        />

        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          {filters.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${
                filter === f ? "bg-[var(--bq-navy)] text-white border-[var(--bq-navy)]" : "bg-white border-[var(--bq-border)] hover:border-[var(--bq-navy)]"
              }`}>
              {f === "all" ? "All" : f.replace("_", " ")}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="overline">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bq-card p-10 text-center">
            <FolderKanban size={28} className="mx-auto text-[var(--bq-text-muted)]" />
            <div className="overline mt-3">No projects</div>
          </div>
        ) : (
          <div className="bq-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bq-bg)]">
                <tr>
                  <th className="text-left px-6 py-3 overline">Project</th>
                  <th className="text-left px-6 py-3 overline hidden md:table-cell">Type</th>
                  <th className="text-left px-6 py-3 overline hidden lg:table-cell">Customer</th>
                  <th className="text-left px-6 py-3 overline">Estimate</th>
                  <th className="text-left px-6 py-3 overline">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-[var(--bq-border)] hover:bg-[var(--bq-bg)]" data-testid={`projects-row-${p.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold">{p.title}</div>
                      <div className="text-xs text-[var(--bq-text-muted)]">{p.address}</div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">{PROJECT_TYPE_LABELS[p.project_type]}</td>
                    <td className="px-6 py-4 hidden lg:table-cell">{p.customer_name}</td>
                    <td className="px-6 py-4">{p.estimate ? `${fmtZAR(p.estimate.min)} – ${fmtZAR(p.estimate.max)}` : "—"}</td>
                    <td className="px-6 py-4"><StatusPill status={p.status} /></td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/projects/${p.id}`} className="text-[var(--bq-orange)] font-semibold text-sm" data-testid={`open-${p.id}`}>Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
